import { t as translate } from 'i18next'
import { GridColumns } from '@mui/x-data-grid-pro'
import { Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'
import { useGetComponents } from '../../../api/components'
import { CenteredSpinner } from '../../../components/Spinner'
import { APIResultCount } from '../../../constant'
import { IComponent } from '../../../generated-types'
import { useFormContext } from 'react-hook-form'
import { Box } from '@mui/material'
import GenericTable from 'src/components/table/GenericTable'
import { renderCaptionedCell } from 'src/components/table/CaptionedCell'
import { SearchBar } from 'src/screens/localities/moorings/PositionTableFilter'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import NoDeviationStepperContent from '../../../assets/svg/no-deviation-stepper-content.svg'
import { getFormattedDate } from 'src/utils'

interface ISelectComponentStepProps {
  customerId: EntityId
  setDisabled: Dispatch<SetStateAction<boolean>>
  handleNext: () => void
  lgModal?: boolean
}
const SelectComponentStep: FC<ISelectComponentStepProps> = ({ customerId, setDisabled, handleNext, lgModal }) => {
  const { getValues, reset } = useFormContext()

  useEffect(() => {
    if (getValues('component')?.id === undefined) {
      setDisabled(true)
    } else {
      setDisabled(false)
    }
  }, [getValues('component')])

  const STATIC_COLUMNS: GridColumns = useMemo(
    () => [
      {
        field: 'productDescription',
        headerName: translate('description', { ns: 'component' }),
        flex: 3.4,
        minWidth: 320,
        cellClassName: 'font-weight-semi-bold',
        renderCell: params =>
          renderCaptionedCell(
            params.value,
            `${translate('productNo', { ns: 'component' })}: ${params.row.productNumber}`,
          ),
      },
      {
        field: 'positionDrawingNumber',
        headerName: translate('drawingNo', { ns: 'component' }),
        flex: 0.65,
        minWidth: 100,
        align: 'center',
      },
      { field: 'quantity', headerName: translate('quantity', { ns: 'component' }), flex: 0.65, minWidth: 90 },
      {
        field: 'installationDate',
        headerName: translate('installationDate', { ns: 'component' }),
        flex: 0.85,
        minWidth: 130,
        renderCell: params =>
          !params.value && typeof params.value !== 'string' ? (
            translate('n/a', { ns: 'common' })
          ) : (
            <h5>{getFormattedDate(new Date(params.value))}</h5>
          ),
      },
    ],
    [],
  )

  const { data: components, isLoading } = useGetComponents(
    customerId,
    getValues('position')?.id as EntityId,
    0,
    APIResultCount.Max,
  )

  const handleRowClick = useCallback(
    params => {
      reset({ ...getValues(), component: params.row as IComponent })
      setDisabled(false)
      handleNext()
    },
    [setDisabled, handleNext],
  )

  const [searchInput, setSearchInput] = useState('')
  const handleSelectInputChange = input => {
    setSearchInput(input)
  }

  const filteredPositions = useMemo(() => {
    return components?.items.filter(
      c =>
        c.productNumber?.includes(searchInput) ||
        c.productDescription.toLocaleLowerCase().includes(searchInput.toLocaleLowerCase()),
    )
  }, [components?.items, searchInput])

  if (isLoading) return <CenteredSpinner />

  return filteredPositions?.length !== 0 ? (
    <Box width={'100%'} sx={{ ml: '10px', mt: '10px' }}>
      <SearchBar
        searchInput={searchInput}
        onChange={handleSelectInputChange}
        placeholder={translate('search', { ns: 'common' })}
        width="100%"
      />
      <GenericTable
        columns={STATIC_COLUMNS}
        data={(filteredPositions as IComponent[]) || []}
        enableToolbar={true}
        height={400}
        enableToolbarFilter={lgModal}
        loading={isLoading}
        onRowClick={handleRowClick}
        selectionModel={getValues('position') ? [getValues('position').id] : []}
        id={'create-deviation-positions'}
        autoHeight={false}
        getRowHeight={() => 72}
        minRowHeight="72px !important"
      />
    </Box>
  ) : (
    <Box className="text-center">
      <EmptyStatusCard
        image={NoDeviationStepperContent}
        header={translate('deviation-component-empty-title', { ns: 'deviation' })}
        content={translate('deviation-component-empty-content', { ns: 'deviation' })}
        imageWidth="89px"
        headerFontSize="18px"
        contentFontSize="14px"
        contentClasses="mt-8"
        imageContainerClasses="p-4"
        contentTopMargin="10px"
      />
    </Box>
  )
}

export default SelectComponentStep
